import {
  MapEventType,
  SourceSpecification,
  LayerSpecification,
} from 'mapbox-gl';
import { TooltipOptions } from 'primevue/tooltip';

// base layers for the map
export enum BaseLayerEnums {
  MAP_SATELLITE = 'satellite',
  MAP_OUTDOORS = 'outdoors',
}
export type BaseLayersType = BaseLayerEnums;

// Base layer configuration for lazy loading
export interface BaseLayerConfig {
  id: string;
  name: string;
  type: 'raster' | 'vector' | 'style';
  style?: string; // For complete style URL (vector)
  url?: string; // For raster tile URL
  sources?: { [key: string]: any }; // For custom sources
  layers?: any[]; // For custom layers
  initialized: boolean;
  visible: boolean;
  // Terrain and sky configuration
  terrain?: boolean;
  sky?: boolean;
}

// single map instance with lazy-loading base layers
export type MapInstance = {
  map: mapboxgl.Map | null;
  baseLayerRegistry: Map<string, BaseLayerConfig>;
  baseLayerSourceIds: Map<string, string[]>; // Track source IDs per base layer
  baseLayerLayerIds: Map<string, string[]>; // Track layer IDs per base layer
  addedListeners: {
    event: MapEventType;
    layer?: string | string[];
    function: any;
  }[];
  loadedTiles: number[];
};

export type MapLayerType = {
  layerMapboxProperties: MapLayerMapboxProperties;
  legendProperties: Array<{
    title: string;
    description?: string;
    icon?: string;
    tooltip?: TooltipOptions;
  }>;
  show: boolean;
  disabled?: boolean;
  propertyHover?: boolean;
  connectedLayers?: MapConnectedLayer[];
};

// Utility type to extract the 'type' property from each type in the union
type ExtractType<T> = T extends { type: infer U } ? U : never;

// Extracting 'type' from each type in the LayerSpecification union
export type LayerTypes = ExtractType<LayerSpecification>;

export type MapLayerMapboxProperties = {
  id: string;
  type: LayerTypes;
  source: string;
  filter?: any[];
  'source-layer'?: string;
  minzoom?: number;
  maxzoom?: number;
  paint: any;
  layout?: any;
};

export type MapConnectedLayer = Partial<MapLayerMapboxProperties> & {
  id: string;
};

/**
 * Represents the type of a map source.
 * @typedef {Object} MapSourceType
 * @property {string} name - The name of the map source.
 * @property {boolean} [reloading] - if set to true, the map source will be reloaded on some occasions.
 * @property {SourceSpecification} sourceMapboxProperties - The properties of the map source specified by Mapbox.
 */
export type MapSourceType = {
  name: string;
  reloading?: boolean;
  sourceMapboxProperties: SourceSpecification;
} & (
  | {
      transformRequestFunction?: undefined;
      transformRequestUrlIdentification?: undefined;
    }
  | {
      transformRequestFunction: mapboxgl.TransformRequestFunction;
      transformRequestUrlIdentification: string;
    }
);

/**
 * Represents the type for hovering over a farm on a map.
 * @property {} feature - the feature hovered over.
 * @property {boolean} [clicked] - indicates whether the feature was clicked.
 * @property {boolean} snapshot - indicates whether the feature is a snapshoted.
 * @property {string} source - the source of the feature.
 * @property {string} [sourceLayer] - the source layer of the feature.
 */
export type HoverFarmType = {
  feature: {
    id: number;
    properties: any;
  };
  clicked?: boolean;
  snapshot: boolean;
  source: string;
  sourceLayer?: string;
};

/**
 * Represents the tooltip information for a map.
 */
export type MapTooltip = {
  show: boolean;
  x: number;
  y: number;
  farms: HoverFarmType[];
};

export type RefLayerEnum = {
  type: 'mvt' | 'geojson';
  test_code: string;
  layer: string;
  data_sources?: {
    [key:string]: {
      [key:string]: string;
    };
  }
};

export type ZoomLevels = {
  mapview_zoom_threshold: number;
  commodity_zoom_threshold: {
    commodity: string;
    zoom_threshold: number;
  }[];
};
